// src/i18n/en/insightV2.ts
// English counterpart of `i18n/es/insightV2.ts` — see there for context.
import metricLabels from './metricLabels';

export const insightV2 = {
  // Metric names by the API's stable id (`resources.insightV2.metrics.<id>`).
  metrics: metricLabels,

  property: {
    label: 'Property',
    placeholder: 'Choose a property',
  },

  stamp: {
    updated: 'Updated %{relative}',
    sample: 'Sample: %{count}',
    insufficientSample: 'Insufficient sample: %{count} case in the period (minimum 20). |||| Insufficient sample: %{count} cases in the period (minimum 20).',
  },

  // What a v2 screen says when the user has no hotel with check-in data:
  // no access to any, or access only to hotels that send none.
  noCheckinData: {
    message: 'None of your hotels has check-in data yet.',
    hint: 'If you need to see another hotel, ask your account administrator for access.',
  },

  remedies: {
    R_FEED: "Not available: this hotel does not receive its reservations from its reservation system. Turn on reservation delivery →",
    R_OUTBOX: 'Unavailable: this device\'s event delivery is not reliable yet. Update the kiosk software →',
    R_N: 'Insufficient sample: fewer than 20 cases in the period.',
    R_PENDING: "Pending confirmation from the hotel's reservation system.",
    R_EVENTTIME: "Not available with the current connection: this hotel's reservation system is checked periodically and does not report its event times.",
    R_PMSFIELD: "This hotel's reservation system does not report this field.",
    R_SIGNAL: 'No signal for %{minutes} min.',
    R_MARKER: 'No chain reference: fewer than three hotels with data.',
    R_NO_BUSINESS_DAY_ROLLOVER: 'Business day not configured: this property has no stated day-close time.',
    R_HOTEL_SOURCE:
      "Not available: there is no record of payments from the hotel's own system to compare with what the self-service point declares.",
  },

  // See i18n/es/insightV2.ts.
  terms: {
    identify: 'Identification',
    reservation_confirm: 'Reservation confirmation',
    room_select: 'Room choice',
    party_capture: 'Guest details',
    document_capture: 'Document reading',
    document_verify: 'Document verification',
    signature: 'Signature',
    terms: 'Terms',
    upsell: 'Extras',
    tourist_tax: 'Tourist tax',
    payment: 'Payment',
    key_delivery: 'Key delivery',
    print: 'Printing',
    survey: 'Survey',
    checkout_balance: 'Check-out balance',
    invoice: 'Invoice',
    summary: 'Summary',
    completed: 'Completed',
    skipped: 'Skipped',
    failed: 'Failed',
    abandoned_here: 'Abandoned here',
    backtracked: 'Went back',
    completed_without_key: 'Completed without key',
    handed_to_desk: 'Sent to reception',
    abandoned: 'Abandoned',
    expired: 'Link expired',
    sin_resultado: 'No result',
    pre_checkin: 'Pre-check-in',
    checkin: 'Check-in',
    key_pickup: 'Key pickup',
    key_activation: 'Key activation',
    checkout: 'Check-out',
    walk_in: 'No reservation (walk-in)',
    purchase: 'Purchases',
    ticket_access: 'Ticket access',
    kiosk: 'Kiosk',
    online: 'Online',
    mobile_kiosk: 'Mobile kiosk',
    air_lobby: 'Air Lobby',
    dispenser: 'Dispenser',
    back_office: 'Reception',
    pms_flag: "Flagged for reception in the hotel's reservation system",
    staff_at_device: 'Staff at the kiosk',
    remote_control: 'Remote assistance',
    phone_call: 'Phone call',
    unsupported_case: 'Case not supported in self-service',
    policy: 'Hotel policy',
    staff_action: 'Staff decision',
    idle_timeout: 'Inactivity',
    back_to_home: 'Went back to start',
    cancel: 'Cancelled by the guest',
    session_closed: 'Session closed',
    staff_reset: 'Reset by staff',
    link_closed: 'Link closed',
    sin_motivo: 'No reason stated',
    sin_codigo: 'No error code',
    pre_checkin_done: 'Pre-check-in already done',
    config_disabled: 'Disabled in the configuration',
    not_applicable: 'Not applicable',
    checked_in: 'Checked in',
    arrival_day_ended_without_checkin: 'No check-in by day end',
    cancelled: 'Cancelled',
    no_show: 'No-show',
    card: 'Card',
    wristband: 'Wristband',
    phone: 'Phone',
    code: 'Code',
    link: 'Link',
    rfid: 'RFID',
    magnetic: 'Magnetic stripe',
    ble: 'Bluetooth',
    nfc: 'NFC',
    none: 'None',
    initial: 'First key',
    duplicate: 'Copy',
    replacement_lost: 'Replacement for a lost key',
    room_change: 'Room change',
    extension: 'Stay extension',
    blocking: 'Blocking',
    non_blocking: 'Non-blocking',
    scanner: 'Document scanner',
    id_scanner: 'Document scanner',
    printer: 'Printer',
    card_dispenser: 'Card dispenser',
    card_encoder: 'Card encoder',
    payment_terminal: 'Card terminal',
    cash_unit: 'Cash unit',
    signature_pad: 'Signature pad',
    qr_reader: 'QR reader',
    camera: 'Camera',
    pms_link: 'Reservation system connection',
    locks_link: 'Locks connection',
    payment_link: 'Payments connection',
    network: 'Network',
    pms: 'Reservation system',
    locks: 'Locks',
    ui: 'Screen',
    cash: 'Cash',
    balance: 'Stay balance',
    deposit: 'Deposit',
    declined: 'Declined',
    accepted: 'Accepted',
    orphaned: 'Orphaned',
    up: 'In service',
    degraded: 'With warnings',
    down: 'Out of service',
    maintenance: 'Under maintenance',
    unknown: 'No signal',
    ok: 'Working',
    fault: 'Faulty',
    absent: 'Not installed',
    push: 'Instant notification',
    scan: 'Periodic check',
  },

  incidentCauses: {
    peripheral: 'Fault in: %{component}',
    peripheral_fault: 'Peripheral fault',
    pms_link_fault: 'No connection to the reservation system',
    device_blocked: 'Device blocked',
    unknown: 'Device incident',
  },
  errorCodes: {
    SCANNER_READ_FAILED: 'Document read failed',
    PRINTER_PAPER_OUT: 'Printer out of paper',
    PRINTER_PAPER_LOW: 'Printer paper running low',
    PMS_TIMEOUT: "The hotel's reservation system did not answer in time",
    PMS_REJECTED: "The hotel's reservation system refused the operation",
    PMS_CHECKIN_FAILED: "The hotel's reservation system did not complete the check-in",
    PMS_RECOVERY_FAILED: 'The check-in could not be recovered',
    PMS_RESERVATION_NOT_FOUND: "Reservation not found in the hotel's reservation system",
    PMS_ROOM_NOT_READY: 'Room not ready',
    PMS_SEND_DOCS_FAILED: "The documents could not be sent to the hotel's reservation system",
    PMS_REVERSE_CHECKIN_FAILED: "The check-in could not be reversed in the hotel's reservation system",
    PMS_REFUND_FAILED: "The refund was not recorded in the hotel's reservation system",
    PMS_PAYMENT_ORPHANED: 'Payment with no reservation to post it to',
    PMS_PAYMENT_ABANDONED: 'Payment with no reservation to post it to',
    PAYMENT_DECLINED: 'Payment declined',
    CARD_DECLINED: 'Card declined',
    PAYMENT_TERMINAL_OFFLINE: 'Card terminal offline',
    REFUND_FAILED: 'Refund failed',
    KEY_ENCODE_FAILED: 'The key could not be encoded',
    KEY_DISPENSE_FAILED: 'The key could not be delivered',
    KD_UID_NOT_REGISTERED: 'Card not registered',
    LOCKS_LINK_DOWN: 'No connection to the locks',
    LOCKS_TIMEOUT: 'The locks did not answer in time',
    CARD_READ_FAILED: 'The card could not be read',
    CARD_WRITE_FAILED: 'The card could not be written',
    CARD_HOPPER_EMPTY: 'Dispenser out of cards',
    CARD_STACK_EMPTY: 'Dispenser out of cards',
    CARD_HOPPER_LOW: 'Few cards left',
    MECH_JAM: 'Card jammed',
    DISPENSER_JAM: 'Card jammed',
    CASH_JAM: 'Cash unit jammed',
    NETWORK_UNREACHABLE: 'No network connection',
    NETWORK_TIMEOUT: 'The network did not answer in time',
    DEVICE_FAULT: 'Device fault',
    SYSTEM_ERROR: 'Internal device error',
    unknown: 'Other error (reference %{code})',
  },

  fields: {
    count: 'Operations',
    amount_minor: 'Amount',
    amount: 'Amount',
    completed_pct: 'Completed share',
    up_or_degraded_pct: 'In service',
    unknown_pct: 'No signal',
    share_pct: 'Share',
    step_kind: 'Step',
    unlinked_count: 'Without original payment',
    unlinked_amount_minor: 'Amount without original payment',
    by_currency: 'By currency',
    counts: 'Journeys by outcome',
    completed_share: 'Share completed',
    up_or_degraded_share: 'In service',
    unknown_share: 'No signal',
    by_cause: 'By cause',
    by_severity: 'By severity',
    planned: 'Planned',
    unplanned: 'Unplanned',
    by_skip_reason: 'Skipped steps by reason',
    contradictions: 'Contradictions',
    minutes: 'Minutes with gaps',
    by_source: 'By source',
    state: 'State',
    pms_vendor: 'Reservation system',
    feed_kind: 'Delivery type',
    scan_interval_minutes: 'Check interval (min)',
    age_seconds: 'Age',
    with_rates: 'With data',
    total: 'Total',
    device_id: 'Device',
    label: 'Device',
    since: 'Since',
    peripheral_id: 'Peripheral',
    kind: 'Type',
    level: 'Level',
    unit: 'Unit',
    low_threshold: 'Low threshold',
    blocking: 'Blocking',
    subject: 'Reference',
    fact_type: 'Data',
    field: 'Field',
    detected_at: 'Detected',
    journey_id: 'Journey',
    pms_reservation_id: 'Reservation',
    median_seconds: 'Median',
    p90_seconds: '9 out of 10',
    rate: 'Rate',
    n: 'Cases',
  },

  breakdown: {
    operations: '%{count} operation |||| %{count} operations',
    step: 'Step',
    cases: '%{count} case |||| %{count} cases',
    ninetyOf: '9 out of 10: %{value}',
    yes: 'Yes',
    no: 'No',
  },

  export: {
    metric: 'Metric',
    value: 'Value',
  },
};

export default insightV2;
